import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RoleRoutingGuard implements CanActivate {
  constructor(private authService: AuthService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.authService.getUserObj().pipe(
      take(1), 
      switchMap(() => {
        // Force fetch roles to ensure we have the latest
        return this.authService.fetchAndCacheRoles(true).pipe(
          switchMap(() => this.authService.getUserObj()),
          take(1), 
          map((updatedUserObj) => this.handleRoleBasedRouting(updatedUserObj))
        );
      })
    );
  }

  private handleRoleBasedRouting(userObj: any): boolean {
    const role = userObj?.ROLE;

    if (role) {
      localStorage.setItem('Role', role);
    }

    // Route based on role priority
    if (
      role === 'Solution Leader' ||
      role === 'Portfolio Leader'
    ) {
      this.router.navigate(['/dashboards/soleader']);
      return false;
    }
    else if (role === 'Quality Champ') {
      if (userObj?.ENABLE_QRS_PROGRAM_FEATURE && userObj?.ENABLE_QRS_QCHAMP_DASHBOARD) {
        this.router.navigate(['/dashboards/qchamp']);
        return false;
      }

      // QC flag is OFF — fall back to secondary role in roleInfo
      const roleInfo: any[] = Array.isArray(userObj?.roleInfo) ? userObj.roleInfo : [];
      const hasLer = roleInfo.some((r: any) => r.UR_ROLE === 'LER');
      const hasLepOrPpmd = roleInfo.some((r: any) => r.UR_ROLE === 'PPMD' || r.UR_ROLE === 'LEP');

      if (hasLer) {
        // Fall back to LER routing logic
        if (userObj?.ENABLE_QRS_PROGRAM_FEATURE && userObj?.ENABLE_QRS_LER_DASHBOARD) {
          this.router.navigate(['/dashboards/ler']);
        } else if (hasLepOrPpmd) {
          if (userObj?.ENABLE_QRS_PROGRAM_FEATURE && userObj?.ENABLE_QRS_LEP_DASHBOARD) {
            this.router.navigate(['/dashboards/engagementleaderdashboard']);
          } else {
            this.router.navigate(['/dashboards/engagementleader']);
          }
        } else {
          this.router.navigate(['/denied']);
        }
        return false;
      } else if (hasLepOrPpmd) {
        // Fall back to LEP/PPMD routing logic
        if (userObj?.ENABLE_QRS_PROGRAM_FEATURE && userObj?.ENABLE_QRS_LEP_DASHBOARD) {
          this.router.navigate(['/dashboards/engagementleaderdashboard']);
        } else {
          this.router.navigate(['/dashboards/engagementleader']);
        }
        return false;
      }

      // No secondary role — allow home
      return true;
    }
    else if (role === 'QRM') {
      if (userObj?.ENABLE_QRM_DASHBOARD) {
        this.router.navigate(['/dashboards/qrmdashboard']);
        return false;
      }

      // QRM dashboard flag is OFF — allow home
      return true;
    }
    else if (role === 'LER') {
      if (userObj?.ENABLE_QRS_PROGRAM_FEATURE && userObj?.ENABLE_QRS_LER_DASHBOARD) {
        this.router.navigate(['/dashboards/ler']);
      } else {
        const hasLepOrPpmd = Array.isArray(userObj?.roleInfo) &&
          userObj.roleInfo.some((r: any) => r.UR_ROLE === 'PPMD' || r.UR_ROLE === 'LEP');

        if (hasLepOrPpmd) {
          if (userObj?.ENABLE_QRS_PROGRAM_FEATURE && userObj?.ENABLE_QRS_LEP_DASHBOARD) {
            this.router.navigate(['/dashboards/engagementleaderdashboard']);
          } else {
            this.router.navigate(['/dashboards/engagementleader']);
          }
        } else {
          this.router.navigate(['/denied']);
        }
      }
      return false;
    }
    else if (role === 'Offering leader') {
      this.router.navigate(['/dashboards/engagementleader']);
      return false;
    }
    else if (role === 'PPMD' || role === 'LEP') {
      // If LEP dashboard is ON → new LEP dashboard for both PPMD and LEP
      // If LEP dashboard is OFF → old EL dashboard for both PPMD and LEP
      if (userObj?.ENABLE_QRS_PROGRAM_FEATURE && userObj?.ENABLE_QRS_LEP_DASHBOARD) {
        this.router.navigate(['/dashboards/engagementleaderdashboard']);
      } else {
        this.router.navigate(['/dashboards/engagementleader']);
      }
      return false;
    }
    else if (role === 'Risk Category Leader') {
      this.router.navigate(['/dashboards/riskleader']);
      return false;
    }

    // Allow access to home for other roles
    return true;
  }
}
